import type {
  FormChatFieldInput,
  FormChatListOption,
} from "gsintegrations";
import type { OnboardingFormChatReferenceData } from "../repositories/onboardingFormChat.repository";

type ReferenceDataKey =
  | "religions"
  | "ranks"
  | "roles"
  | "categories"
  | "lines"
  | "entities";

const FIELD_REFERENCE_KEYS: Record<string, ReferenceDataKey> = {
  religionId: "religions",
  rankId: "ranks",
  roleId: "roles",
  category: "categories",
  lineId: "lines",
  entityId: "entities",
};

const FIELD_INPUT_LABELS: Record<
  string,
  Pick<FormChatFieldInput, "openListLabel" | "changeLabel" | "searchable">
> = {
  religionId: {
    openListLabel: "Ver religiões",
    changeLabel: "Alterar",
    searchable: true,
  },
  rankId: {
    openListLabel: "Ver graus",
    changeLabel: "Alterar",
    searchable: true,
  },
  roleId: {
    openListLabel: "Ver funções",
    changeLabel: "Alterar",
    searchable: true,
  },
  category: {
    openListLabel: "Ver categorias",
    changeLabel: "Alterar",
    searchable: true,
  },
  lineId: {
    openListLabel: "Ver linhas",
    changeLabel: "Alterar",
    searchable: true,
  },
  entityId: {
    openListLabel: "Ver entidades",
    changeLabel: "Alterar",
    searchable: true,
  },
};

export const resolveFieldOptions = (
  activeField: string | undefined,
  referenceData?: OnboardingFormChatReferenceData & {
    categories?: Array<{ id: string; name: string }>;
    lines?: Array<{ id: string; name: string }>;
    entities?: Array<{ id: string; name: string }>;
  },
): FormChatListOption[] => {
  if (!activeField) return [];

  const referenceKey = FIELD_REFERENCE_KEYS[activeField];
  if (!referenceKey) return [];

  const options = referenceData?.[referenceKey];
  if (!options?.length) return [];

  return options.map((option) => ({
    id: option.id,
    label: option.name,
  }));
};

// The API only sends a text input for these, so the numeric keyboard and BRL mask are set here.
const NUMBER_FIELD_INPUTS: Record<string, FormChatFieldInput> = {
  quantity: {
    type: "number",
    field: "quantity",
    numberFormat: "decimal",
    allowFreeText: true,
  },
  minimumQuantity: {
    type: "number",
    field: "minimumQuantity",
    numberFormat: "decimal",
    allowFreeText: true,
  },
  estimatedUnitCost: {
    type: "number",
    field: "estimatedUnitCost",
    numberFormat: "currency",
    allowFreeText: true,
    quickReplies: ["Pular"],
  },
};

export const resolveFieldInput = (
  activeField: string | undefined,
  referenceData?: OnboardingFormChatReferenceData & {
    categories?: Array<{ id: string; name: string }>;
    lines?: Array<{ id: string; name: string }>;
    entities?: Array<{ id: string; name: string }>;
  },
  serverFieldInput?: FormChatFieldInput,
): FormChatFieldInput | undefined => {
  if (activeField && NUMBER_FIELD_INPUTS[activeField]) {
    return NUMBER_FIELD_INPUTS[activeField];
  }
  if (serverFieldInput) return serverFieldInput;
  if (!activeField || !FIELD_REFERENCE_KEYS[activeField]) return undefined;

  const labels = FIELD_INPUT_LABELS[activeField];
  const topSuggestions = resolveFieldOptions(activeField, referenceData)
    .slice(0, 4)
    .map((option) => option.label);

  return {
    type: "list_select",
    field: activeField,
    searchable: labels?.searchable,
    openListLabel: labels?.openListLabel,
    changeLabel: labels?.changeLabel,
    allowFreeText: false,
    topSuggestions,
  };
};

// Mirrors the API formChatFieldInput registry so local edit prompts render the same widget as creation.
const EDIT_FIELD_INPUTS: Record<string, FormChatFieldInput> = {
  date: { type: "date", field: "date", allowFreeText: true },
  startTime: { type: "time", field: "startTime", allowFreeText: true },
  endTime: { type: "time", field: "endTime", allowFreeText: true },
  isTemplate: {
    type: "quick_replies",
    field: "isTemplate",
    quickReplies: ["Sim", "Não"],
  },
  isAllDay: {
    type: "quick_replies",
    field: "isAllDay",
    quickReplies: ["Sim", "Não"],
  },
  isInTerreiro: {
    type: "quick_replies",
    field: "isInTerreiro",
    quickReplies: ["Sim", "Não"],
  },
  activityType: {
    type: "quick_replies",
    field: "activityType",
    quickReplies: ["Atividade", "Ritual"],
  },
  image: {
    type: "image",
    field: "image",
    allowFreeText: false,
    quickReplies: ["Pular"],
  },
};

export const resolveEditFieldInput = (
  activeField: string | undefined,
  referenceData?: Parameters<typeof resolveFieldInput>[1],
): FormChatFieldInput | undefined =>
  resolveFieldInput(activeField, referenceData) ??
  (activeField ? EDIT_FIELD_INPUTS[activeField] : undefined);

export const resolveSelectedOption = (
  activeField: string | undefined,
  formState: Record<string, unknown>,
  fieldOptions: FormChatListOption[],
): FormChatListOption | undefined => {
  if (!activeField) return undefined;
  const selectedId = formState[activeField];
  if (typeof selectedId !== "string" || !selectedId) return undefined;
  return fieldOptions.find((option) => option.id === selectedId);
};

export const isListSelectField = (field: string | undefined): boolean =>
  Boolean(field && FIELD_REFERENCE_KEYS[field]);
